import { useCallback, useRef } from 'react';

import { env } from '@/config/env';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
}

interface GoogleTokenClient {
  requestAccessToken: () => void;
}

/** GIS reports popup dismissal/failure here — never through `callback`. */
interface GoogleErrorResponse {
  type?: string;
}

interface GoogleAccountsOAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
    error_callback?: (error: GoogleErrorResponse) => void;
  }) => GoogleTokenClient;
}

/**
 * The user deliberately dismissed the Google popup. Callers should reset their loading state but
 * stay silent — a cancellation is a choice, not an error worth a toast.
 */
export class GoogleSignInCancelledError extends Error {
  constructor() {
    super('Google sign-in was cancelled.');
    this.name = 'GoogleSignInCancelledError';
  }
}

declare global {
  interface Window {
    google?: { accounts: { oauth2: GoogleAccountsOAuth2 } };
  }
}

let scriptLoadPromise: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  scriptLoadPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Wraps Google Identity Services' token-client popup flow (safe for a pure SPA — unlike
 * LinkedIn's current OIDC product, Google's client-side flow can yield an access token without a
 * client-secret exchange). Disabled when `VITE_GOOGLE_CLIENT_ID` isn't set — no app is registered
 * for local dev today.
 */
export function useGoogleOAuth() {
  const tokenClientRef = useRef<GoogleTokenClient | null>(null);
  // The token client is created once and reused, so its callbacks must NOT close over a single
  // attempt's resolve/reject — the second click would settle the FIRST attempt's promise and leave
  // the new one pending forever. Every attempt publishes its handlers here instead.
  const pendingRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const isConfigured = Boolean(env.googleClientId);

  const requestAccessToken = useCallback((): Promise<string> => {
    if (!isConfigured) {
      return Promise.reject(new Error('Google sign-in is not configured in this environment.'));
    }

    return loadGoogleIdentityServices().then(
      () =>
        new Promise<string>((resolve, reject) => {
          // A previous attempt that never settled must not linger and swallow this one's outcome.
          pendingRef.current?.reject(new GoogleSignInCancelledError());
          pendingRef.current = { resolve, reject };

          const takePending = () => {
            const pending = pendingRef.current;
            pendingRef.current = null;
            return pending;
          };

          tokenClientRef.current ??= window.google!.accounts.oauth2.initTokenClient({
            client_id: env.googleClientId,
            scope: 'openid email profile',
            callback: (response) => {
              const pending = takePending();
              if (!pending) return;
              if (response.access_token) {
                pending.resolve(response.access_token);
              } else {
                pending.reject(new Error(response.error ?? 'Google sign-in failed.'));
              }
            },
            // GIS does NOT call `callback` when the popup is dismissed or fails to open. Without
            // this the promise never settles and the caller's loading state sticks on forever,
            // leaving the button permanently disabled until the page is reloaded.
            error_callback: (error) => {
              const pending = takePending();
              if (!pending) return;
              pending.reject(
                error?.type === 'popup_failed_to_open'
                  ? new Error('Google sign-in popup was blocked. Allow popups and try again.')
                  : new GoogleSignInCancelledError(),
              );
            },
          });
          tokenClientRef.current.requestAccessToken();
        }),
    );
  }, [isConfigured]);

  return { requestAccessToken, isConfigured };
}
